import { PhysioLayout } from "@/components/layout/physio-layout";
import { useState, useEffect } from "react";
import { BookOpen, CheckCircle, Clock, Circle } from "lucide-react";
import { Link } from "wouter";

const CSP_DIMENSIONS = [
  {
    key: "keeping_up_to_date",
    title: "Keeping Up to Date",
    description: "Maintaining and developing your professional knowledge and staying current with evidence-based practice.",
    examples: [
      "Reading peer-reviewed journals (e.g. Physiotherapy, JOSPT, ARD)",
      "Attending CSP webinars and CPD events",
      "Following NICE MSK guideline updates",
      "Engaging with ACPR position statements and resources",
    ],
    colour: "emerald",
  },
  {
    key: "developing_knowledge",
    title: "Developing Knowledge and Skills",
    description: "Actively building new competencies through structured learning and practice.",
    examples: [
      "Postgraduate courses (e.g. MSc modules, manual therapy diplomas)",
      "Specialist clinical training (e.g. hydrotherapy, dry needling)",
      "iCSP online learning modules",
      "Clinical skills workshops and simulation",
    ],
    colour: "teal",
  },
  {
    key: "reflecting",
    title: "Reflecting on Practice",
    description: "Critical reflection on your clinical and professional practice to drive improvement.",
    examples: [
      "Gibbs or Rolfe reflective cycle entries in your CPD log",
      "Clinical supervision sessions",
      "Peer observation and feedback",
      "Significant event analysis",
    ],
    colour: "blue",
  },
  {
    key: "contributing",
    title: "Contributing to the Profession",
    description: "Sharing knowledge, supporting others, and advancing physiotherapy as a profession.",
    examples: [
      "Supervising or mentoring junior physiotherapists",
      "ACPR committee membership or CSP branch involvement",
      "Presenting at departmental journal clubs or grand rounds",
      "Audit, quality improvement, or service evaluation",
    ],
    colour: "violet",
  },
];

const COLOUR_MAP: Record<string, { bg: string; border: string; text: string; badge: string; icon: string }> = {
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", badge: "bg-emerald-100 text-emerald-700", icon: "text-emerald-600" },
  teal: { bg: "bg-teal-50", border: "border-teal-200", text: "text-teal-800", badge: "bg-teal-100 text-teal-700", icon: "text-teal-600" },
  blue: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800", badge: "bg-blue-100 text-blue-700", icon: "text-blue-600" },
  violet: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-800", badge: "bg-violet-100 text-violet-700", icon: "text-violet-600" },
};

interface CpdActivity {
  id: number;
  title: string;
  date: string;
  category: string;
  hours: number;
}

export default function CspFrameworkTracker() {
  const [activities, setActivities] = useState<CpdActivity[]>([]);

  useEffect(() => {
    const year = new Date().getFullYear();
    fetch(`/api/physio/cpd?year=${year}`).then(r => r.ok ? r.json() : []).then(setActivities);
  }, []);

  const byCategory = activities.reduce((acc, a) => {
    acc[a.category] = (acc[a.category] ?? 0) + a.hours;
    return acc;
  }, {} as Record<string, number>);

  const dimensionHours: Record<string, number> = {
    keeping_up_to_date: byCategory["educational"] ?? 0,
    developing_knowledge: byCategory["educational"] ?? 0,
    reflecting: byCategory["professional"] ?? 0,
    contributing: byCategory["leadership"] ?? 0,
  };

  const totalHours = Object.values(byCategory).reduce((s, v) => s + v, 0);
  const coveredDimensions = CSP_DIMENSIONS.filter(d => dimensionHours[d.key] > 0).length;

  return (
    <PhysioLayout title="CSP CPD Framework Tracker">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <BookOpen className="h-8 w-8 text-emerald-300" />
            <div>
              <h2 className="text-xl font-bold">CSP CPD Framework</h2>
              <p className="text-emerald-300 text-sm">Chartered Society of Physiotherapy · Four CPD Dimensions</p>
            </div>
          </div>
          <p className="text-emerald-200 text-sm leading-relaxed">
            The CSP recommends structuring your CPD around four dimensions. Activities in your log are mapped to each dimension below. Aim to have activity across all four dimensions across your 2-year HCPC renewal cycle.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-xl font-bold">{totalHours.toFixed(1)}</p>
              <p className="text-xs text-emerald-300">Total CPD hours this year</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-xl font-bold">{coveredDimensions}/4</p>
              <p className="text-xs text-emerald-300">Dimensions covered</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {CSP_DIMENSIONS.map((dim) => {
            const c = COLOUR_MAP[dim.colour];
            const hours = dimensionHours[dim.key];
            const hasCoverage = hours > 0;
            return (
              <div key={dim.key} className={`rounded-2xl border p-6 ${c.bg} ${c.border}`}>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-3">
                    {hasCoverage
                      ? <CheckCircle className={`h-5 w-5 shrink-0 ${c.icon}`} />
                      : <Circle className="h-5 w-5 shrink-0 text-slate-300" />
                    }
                    <div>
                      <h3 className={`font-bold ${c.text}`}>{dim.title}</h3>
                      <p className="text-sm text-slate-600 mt-0.5">{dim.description}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {hasCoverage
                      ? <span className={`text-sm font-bold px-3 py-1 rounded-full ${c.badge}`}>{hours.toFixed(1)} hrs</span>
                      : <span className="flex items-center gap-1 text-xs text-slate-400"><Clock className="h-3.5 w-3.5" /> No activities yet</span>
                    }
                  </div>
                </div>
                <div className="mt-3">
                  <p className="text-xs font-semibold text-slate-500 mb-2">Suggested activities:</p>
                  <div className="flex flex-wrap gap-2">
                    {dim.examples.map((ex) => (
                      <span key={ex} className="text-xs bg-white/60 border border-white/80 rounded-full px-3 py-1 text-slate-700">{ex}</span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-bold text-slate-900 mb-2">Category Mapping</h3>
          <p className="text-sm text-slate-500 mb-4">
            Your CPD log categories are mapped to CSP dimensions as follows. Log activities in the CPD log and they will appear automatically here.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { cat: "Educational (Courses / Reading)", maps: "Keeping Up to Date + Developing Knowledge" },
              { cat: "Professional (Governance / Ethics)", maps: "Reflecting on Practice" },
              { cat: "Leadership (Audit / Supervision)", maps: "Contributing to the Profession" },
              { cat: "Clinical (Patient Care / Skills)", maps: "Developing Knowledge and Skills" },
            ].map(({ cat, maps }) => (
              <div key={cat} className="bg-slate-50 rounded-xl p-3">
                <p className="text-sm font-medium text-slate-700">{cat}</p>
                <p className="text-xs text-slate-500 mt-0.5">→ {maps}</p>
              </div>
            ))}
          </div>
          <Link href="/physio/cpd">
            <button className="mt-4 text-sm text-emerald-700 hover:text-emerald-800 font-medium">
              Go to CPD Activity Log →
            </button>
          </Link>
        </div>

        <p className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>CSP CPD Framework:</strong> Content aligned with the Chartered Society of Physiotherapy CPD framework (csp.org.uk). Dimension mapping is approximate — you should categorise your own activities using your professional judgement.
        </p>
      </div>
    </PhysioLayout>
  );
}
